/**
 * The HUD's line icons (spec v2 §5): one 24-unit grid, one stroke weight, drawn in the current
 * text colour. One icon per meaning, never two meanings for one icon. Decorative: every button
 * that shows one carries its accessible name and tooltip.
 */
import type { ReactNode } from 'react';

export type IconName =
  /** Movement: one sailing ship. */
  | 'sail'
  /** The fleet: several ships. */
  | 'fleet'
  /** Fire: a cannon. */
  | 'cannon'
  /** A volley: several cannons. */
  | 'volley'
  | 'info'
  /** Centre the camera on something: a gunsight. */
  | 'centre'
  /** The log: a book. */
  | 'log'
  | 'settings'
  | 'help'
  | 'menu'
  | 'close'
  | 'back'
  | 'replay'
  | 'undo'
  | 'plus'
  | 'minus'
  /** Show the whole map. */
  | 'fit'
  | 'map'
  /** Cast the wind. */
  | 'wind'
  /** End the turn. */
  | 'hourglass'
  /** Skip sailing, go to firing. */
  | 'skip'
  /** Raise sails after firing: back to choosing the squadron. */
  | 'raise'
  /** In-port move. */
  | 'anchor'
  /** Repair in dock. */
  | 'wrench'
  /** Repair a fort. */
  | 'hammer'
  | 'stop'
  | 'fort'
  /** In the squadron. */
  | 'flag'
  | 'check'
  | 'heart'
  /** A hit. */
  | 'burst'
  /** Squadron: everyone. */
  | 'selectAll'
  /** Squadron: everyone not lying in port. */
  | 'leavePort'
  /** Squadron: no one. */
  | 'selectNone'
  /** A hole in the hull: damage taken. */
  | 'hole'
  /** Range in cells. */
  | 'range'
  /** Calm: still water. */
  | 'calm'
  /** Storm drift. */
  | 'storm'
  /** Open / fold a panel. */
  | 'chevronUp'
  | 'chevronDown';

const PATHS: Readonly<Record<IconName, ReactNode>> = {
  sail: (
    <>
      <path d="M4 17h16l-2 3H6z" />
      <path d="M12 3v14" />
      <path d="M12 4c3.5 2 5 6 5 10h-5" />
      <path d="M12 6.5c-2.5 2-3.8 4.8-3.8 7.5H12" />
    </>
  ),
  fleet: (
    <>
      <path d="M11 17h11l-1.6 3h-7.8z" />
      <path d="M16.5 5v12M16.5 6c2.6 1.8 3.8 4.8 3.8 8.5h-3.8M16.5 8.5c-1.8 1.5-2.7 3.5-2.7 6h2.7" />
      <path d="M2 14h8l-1.2 2.4H3.2z" />
      <path d="M6 5.5V14M6 6.5c2 1.3 2.8 3.4 2.8 6H6" />
    </>
  ),
  cannon: (
    <>
      <path d="M4.2 12.6L16.4 8l1.5 3.7L5.7 16.3z" />
      <path d="M18.6 8.6l2.2-.8M19.4 11.2l2.1-.2" />
      <circle cx="8.5" cy="17.2" r="2.8" />
      <path d="M3 20.5h11" />
    </>
  ),
  volley: (
    <>
      <path d="M2.8 10.4l9-3.4 1.2 2.9-9 3.4z" />
      <circle cx="5.6" cy="14" r="2" />
      <path d="M9 16.4l9-3.4 1.2 2.9-9 3.4z" />
      <circle cx="11.8" cy="20" r="2" />
      <path d="M14 6.2l2-.9M20.2 12.2l2-.9" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.3" />
    </>
  ),
  centre: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v5M12 17v5M2 12h5M17 12h5" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ),
  log: (
    <>
      <path d="M3 5.5c3-1 6-.8 9 1.2 3-2 6-2.2 9-1.2v13c-3-1-6-.8-9 1.2-3-2-6-2.2-9-1.2z" />
      <path d="M12 6.7v13" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8l1.5 2.1 2.6-.5.8 2.5 2.5.8-.5 2.6 2.1 1.5-2.1 1.5.5 2.6-2.5.8-.8 2.5-2.6-.5-1.5 2.1-1.5-2.1-2.6.5-.8-2.5-2.5-.8.5-2.6L2.8 12l2.1-1.5-.5-2.6 2.5-.8.8-2.5 2.6.5z" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.6v.6M12 17.2v.3" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  back: <path d="M19 12H5M11 6l-6 6 6 6" />,
  replay: (
    <>
      <path d="M4 12a8 8 0 1 0 2.3-5.6" />
      <path d="M4 3v4h4" />
      <path d="M10 9l5 3-5 3z" />
    </>
  ),
  undo: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  fit: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  map: (
    <>
      <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
      <path d="M9 4v14M15 6v14" />
    </>
  ),
  wind: (
    <>
      <path d="M3 8.5h11.5a3 3 0 1 0-3-3" />
      <path d="M3 12.5h15.5a3 3 0 1 1-3 3" />
      <path d="M3 16.5h7" />
    </>
  ),
  hourglass: (
    <>
      <path d="M6.5 3h11M6.5 21h11" />
      <path d="M8 3c0 5.2 8 5 8 9s-8 3.8-8 9M16 3c0 5.2-8 5-8 9s8 3.8 8 9" />
      <path d="M10 18.5h4" />
    </>
  ),
  skip: <path d="M5 6l6 6-6 6M12.5 6l6 6-6 6" />,
  raise: (
    <>
      <path d="M12 20V7M7.5 11.5L12 7l4.5 4.5" />
      <path d="M5 3.5h14" />
    </>
  ),
  anchor: (
    <>
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v14M8 10h8M4 13a8 8 0 0 0 16 0" />
    </>
  ),
  wrench: (
    <path d="M14.5 3.5a4.5 4.5 0 0 0-4.2 6.1L3.8 16.1l4.1 4.1 6.5-6.5a4.5 4.5 0 0 0 6.1-4.2l-2.8 2.8-3.1-.6-.6-3.1 2.8-2.8a4.5 4.5 0 0 0-2.3-.3z" />
  ),
  hammer: (
    <>
      <path d="M12.6 3.5l7.9 7.9-2.6 2.6L10 6.1z" />
      <path d="M13 9.1L4 18.1l2.9 2.9 9-9" />
    </>
  ),
  stop: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9h6v6H9z" />
    </>
  ),
  fort: (
    <>
      <path d="M4 21V9h3v2h2V9h6v2h2V9h3v12z" />
      <path d="M10 21v-4a2 2 0 0 1 4 0v4" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h12l-2.5 4L17 12H5" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  burst: (
    <path d="M12 3l1.8 5.2L19 6l-2.6 4.8L21.5 12l-5.1 1.6L19 18.5l-5-2.1L12 21.5l-1.9-5.1-5 2.1 2.5-4.9L2.5 12l5.1-1.2L5 6l5.2 2.2z" />
  ),
  selectAll: (
    <>
      <path d="M4 4h16v16H4z" />
      <path d="M8 12.5l3 3 5-6" />
    </>
  ),
  leavePort: (
    <>
      <path d="M9 4H4v16h5" />
      <path d="M11 12h10M17 8l4 4-4 4" />
    </>
  ),
  selectNone: (
    <>
      <path d="M4 4h16v16H4z" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  hole: (
    <>
      <path d="M3 9h18l-2.5 9h-13z" />
      <path d="M12 11.5l1 1.8 2-.6-1 1.8 1.4 1.5-2-.1-.6 2-.8-1.9-2 .5 1.2-1.7-1.4-1.5 2 .1z" />
    </>
  ),
  range: (
    <>
      <path d="M4 7v10" />
      <path d="M4 12h15M16 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),
  calm: <path d="M3 10h18M3 14h18" />,
  storm: <path d="M13.5 2.5L6.5 13h5l-2 8.5 8-11.5h-5z" />,
  chevronUp: <path d="M6 15l6-6 6 6" />,
  chevronDown: <path d="M6 9l6 6 6-6" />,
  heart: <path d="M12 20s-7.5-4.6-7.5-10A4.2 4.2 0 0 1 12 7.5 4.2 4.2 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z" />,
};

export function Icon({ name, size = 20 }: { name: IconName; size?: number | string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
